import { useEffect, useMemo, useRef, useState } from "react";
import { TransitMap } from "./Map";
import { StringlineDiagram } from "./StringlineDiagram";
import { Messages } from "./Messages";
import { PlatformDisplay } from "./PlatformDisplay";
import { DepartureBoard } from "./DepartureBoard";
import { PlatformSign } from "./PlatformSign";
import { TokyoLCD } from "./TokyoLCD";
import { NYCCountdown } from "./NYCCountdown";
import { AmtrakSolari } from "./AmtrakSolari";
import { Headways } from "./Headways";
import { Bunching } from "./Bunching";
import BingoCard from "./BingoCard";
import { AlertsBadge } from "./Alerts";
import { TimeSlider, useDebouncedHistoryFetch } from "./TimeSlider";
import { useVehicleStream, type Vehicle } from "./useVehicleStream";
import { useTheme, type ThemePref } from "./useTheme";
import {
  createHistoryStore,
  ingestSnapshot,
  seedHistory,
  type HistoryStore,
} from "./positionHistory";

type LineSummary = {
  key: string;
  label: string;
  color: string;
  count: number;
  destinations: Array<[string, number]>;
};

type Tab =
  | "map"
  | "stringline"
  | "tokyo"
  | "nyc"
  | "amtrak"
  | "platform"
  | "board"
  | "sign"
  | "headways"
  | "bunching"
  | "messages"
  | "bingo";

const HISTORY_WINDOW_SEC = 30 * 60;

const CONNECTING_MESSAGES = [
  "○ connecting…",
  "○ checking tracks…",
  "○ finding trains…",
  "○ almost there…",
];

const DEPLOYED_COMMIT =
  (import.meta.env.VITE_GIT_SHA as string | undefined)?.trim() || "dev";
const COMMIT_REPO_URL = "https://github.com/zarenner/link";

function DeployedCommit() {
  const isDev = DEPLOYED_COMMIT === "dev";
  const href = isDev ? undefined : `${COMMIT_REPO_URL}/commit/${DEPLOYED_COMMIT}`;
  const label = `Deployed commit ${DEPLOYED_COMMIT}`;
  return isDev ? (
    <div className="deployed-commit" title={label} aria-label={label}>
      {DEPLOYED_COMMIT}
    </div>
  ) : (
    <a
      className="deployed-commit"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
    >
      {DEPLOYED_COMMIT}
    </a>
  );
}

const THEME_ICON: Record<ThemePref, string> = {
  light: "☀︎",
  dark: "☾",
  system: "◐",
};
const THEME_LABEL: Record<ThemePref, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

function ThemeToggle({
  pref,
  onCycle,
}: {
  pref: ThemePref;
  onCycle: () => void;
}) {
  const title = `Theme: ${THEME_LABEL[pref]} (click to cycle)`;
  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={onCycle}
      title={title}
      aria-label={title}
    >
      {THEME_ICON[pref]}
    </button>
  );
}

export default function App() {
  const { pref: themePref, effective: themeEffective, cyclePref: cycleTheme } =
    useTheme();
  const [liveVehicles, setLiveVehicles] = useState<Vehicle[]>([]);
  const [historicalVehicles, setHistoricalVehicles] = useState<Vehicle[]>([]);
  // historyTs is the unix-seconds timestamp the user has scrubbed to. null
  // means "live" — the SSE stream drives the map and the slider sits at the
  // right edge.
  const [historyTs, setHistoryTs] = useState<number | null>(null);
  const [connected, setConnected] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [connectingIdx, setConnectingIdx] = useState(0);
  const [tab, setTab] = useState<Tab>("map");
  // Tick once a second so the "n s ago" readout in the panel stays fresh
  // without re-rendering the whole map.
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  const vehicles = historyTs === null ? liveVehicles : historicalVehicles;

  // Position history is mutated in place (cheap), and we expose a counter
  // so consumers can re-render at the cadence of upstream updates.
  const historyRef = useRef<HistoryStore>(createHistoryStore());
  const [historyVersion, setHistoryVersion] = useState(0);

  useVehicleStream({
    onSnapshot: (snap) => {
      const next = snap.vehicles ?? [];
      setLiveVehicles(next);
      // Always feed the rolling history buffer so the Stringline diagram and
      // ghost trails stay continuous, regardless of scrub state.
      const changed = ingestSnapshot(
        historyRef.current,
        next,
        Math.floor(Date.now() / 1000),
        HISTORY_WINDOW_SEC,
      );
      if (changed) setHistoryVersion((v) => v + 1);
    },
    onStatusChange: setConnected,
  });

  // When the user scrubs the time slider, fetch the reconstructed snapshot
  // for that instant. Debounced inside the hook so dragging is cheap.
  useDebouncedHistoryFetch(historyTs, (vs) => {
    setHistoricalVehicles(vs as Vehicle[]);
  });

  useEffect(() => {
    if (connected) {
      const n = liveVehicles.length;
      document.title = `link · ${n} ${n === 1 ? "train" : "trains"} live`;
    } else {
      document.title = "link · transit";
    }
  }, [connected, liveVehicles.length]);

  useEffect(() => {
    const id = window.setInterval(
      () => setNow(Math.floor(Date.now() / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, []);

  // Periodically expire stale samples even if no new snapshot arrived, so
  // a transient backend stall doesn't leave the Stringline diagram showing
  // ancient lines.
  useEffect(() => {
    const id = window.setInterval(() => {
      const changed = ingestSnapshot(
        historyRef.current,
        [],
        Math.floor(Date.now() / 1000),
        HISTORY_WINDOW_SEC,
      );
      if (changed) setHistoryVersion((v) => v + 1);
    }, 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Seed the live history buffer once on mount with up to HISTORY_WINDOW_SEC
  // of persisted samples from the backend, so the Stringline diagram shows
  // a populated trail immediately instead of starting blank and only
  // filling in as new SSE snapshots arrive.
  useEffect(() => {
    const ctl = new AbortController();
    (async () => {
      try {
        const res = await fetch(
          `/api/realtime/vehicles/history?window=${HISTORY_WINDOW_SEC}`,
          { signal: ctl.signal },
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          vehicles?: Array<{
            vehicle_id: string;
            route_id?: string;
            route_short_name?: string;
            route_color?: string;
            samples: Array<{
              ts: number;
              lat: number;
              lon: number;
              bearing?: number;
              trip_id?: string;
              trip_headsign?: string;
            }>;
          }>;
        };
        const seed = (data.vehicles ?? [])
          .filter((v) => v.vehicle_id && v.route_id)
          .map((v) => ({
            vehicleId: v.vehicle_id,
            routeId: v.route_id ?? "",
            routeColor: v.route_color,
            routeShortName: v.route_short_name,
            samples: (v.samples ?? []).map((s) => ({
              ts: s.ts,
              lat: s.lat,
              lon: s.lon,
              bearing: s.bearing,
              routeId: v.route_id ?? "",
              routeColor: v.route_color,
              routeShortName: v.route_short_name,
              tripId: s.trip_id,
              tripHeadsign: s.trip_headsign,
            })),
          }));
        const changed = seedHistory(
          historyRef.current,
          seed,
          Math.floor(Date.now() / 1000),
          HISTORY_WINDOW_SEC,
        );
        if (changed) setHistoryVersion((v) => v + 1);
      } catch {
        // AbortError or network blip — the live SSE stream will still
        // populate history going forward; we just won't have backfill.
      }
    })();
    return () => ctl.abort();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === "?") setShowHelp((v) => !v);
      if (e.key === "f" || e.key === "F") setFitTrigger((n) => n + 1);
      if (e.key === "Escape") setShowHelp(false);
      if (e.key === "1") setTab("map");
      if (e.key === "2") setTab("stringline");
      if (e.key === "3") setTab("tokyo");
      if (e.key === "4") setTab("nyc");
      if (e.key === "5") setTab("amtrak");
      if (e.key === "6") setTab("platform");
      if (e.key === "7") setTab("board");
      if (e.key === "8") setTab("sign");
      if (e.key === "9") setTab("headways");
      if (e.key === "0") setTab("bunching");
      if (e.key === "-") setTab("messages");
      if (e.key === "=") setTab("bingo");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (connected) return;
    const id = window.setInterval(
      () => setConnectingIdx((i) => (i + 1) % CONNECTING_MESSAGES.length),
      2500,
    );
    return () => window.clearInterval(id);
  }, [connected]);

  // When the user switches back to the map tab, maplibre needs to know
  // its container has changed size (it was display:none, which collapses
  // the canvas). Dispatching a resize is the simplest way; maplibre
  // listens for it.
  useEffect(() => {
    if (tab === "map") {
      // Defer a tick so the layout has actually re-flowed before we resize.
      const id = window.setTimeout(
        () => window.dispatchEvent(new Event("resize")),
        0,
      );
      return () => window.clearTimeout(id);
    }
  }, [tab]);

  // Group vehicles by route so the panel can show a per-line breakdown
  // (e.g. "1 Line: 14 trains, 2 Line: 6 trains") plus per-line destination
  // counts beneath each row.
  const lines: LineSummary[] = useMemo(() => {
    type Acc = Omit<LineSummary, "destinations"> & {
      destCounts: Map<string, number>;
    };
    const byRoute = new Map<string, Acc>();
    for (const v of vehicles) {
      const key = v.route_id || v.route_short_name || "—";
      let acc = byRoute.get(key);
      if (!acc) {
        acc = {
          key,
          label: v.route_short_name || v.route_id || "Unknown",
          color: v.route_color ? `#${v.route_color}` : "#1f78ff",
          count: 0,
          destCounts: new Map<string, number>(),
        };
        byRoute.set(key, acc);
      }
      acc.count++;
      if (v.trip_headsign) {
        acc.destCounts.set(
          v.trip_headsign,
          (acc.destCounts.get(v.trip_headsign) ?? 0) + 1,
        );
      }
    }
    return Array.from(byRoute.values())
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true }),
      )
      .map(({ destCounts, ...rest }) => ({
        ...rest,
        destinations: Array.from(destCounts.entries()).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
      }));
  }, [vehicles]);

  const newestTs = vehicles.reduce((m, v) => Math.max(m, v.ts || 0), 0);
  const ageSec = newestTs > 0 ? Math.max(0, now - newestTs) : null;

  return (
    <div className="app">
      <div className="tabs" role="tablist" aria-label="Views">
        <button
          role="tab"
          aria-selected={tab === "map"}
          className={`tab ${tab === "map" ? "active" : ""}`}
          onClick={() => setTab("map")}
          title="Map view (1)"
        >
          Map
        </button>
        <button
          role="tab"
          aria-selected={tab === "stringline"}
          className={`tab ${tab === "stringline" ? "active" : ""}`}
          onClick={() => setTab("stringline")}
          title="Stringline diagram (2)"
        >
          Stringline
        </button>
        <button
          role="tab"
          aria-selected={tab === "tokyo"}
          className={`tab ${tab === "tokyo" ? "active" : ""}`}
          onClick={() => setTab("tokyo")}
          title="Tokyo JR LCD (3)"
        >
          Tokyo JR
        </button>
        <button
          role="tab"
          aria-selected={tab === "nyc"}
          className={`tab ${tab === "nyc" ? "active" : ""}`}
          onClick={() => setTab("nyc")}
          title="NYC subway countdown clock (4)"
        >
          NYC Countdown
        </button>
        <button
          role="tab"
          aria-selected={tab === "amtrak"}
          className={`tab ${tab === "amtrak" ? "active" : ""}`}
          onClick={() => setTab("amtrak")}
          title="Amtrak Solari split-flap (5)"
        >
          Amtrak Solari
        </button>
        <button
          role="tab"
          aria-selected={tab === "platform"}
          className={`tab ${tab === "platform" ? "active" : ""}`}
          onClick={() => setTab("platform")}
          title="Seoul Metro displays (6)"
        >
          Seoul Metro
        </button>
        <button
          role="tab"
          aria-selected={tab === "board"}
          className={`tab ${tab === "board" ? "active" : ""}`}
          onClick={() => setTab("board")}
          title="Departure board (7)"
        >
          NS Timetable
        </button>
        <button
          role="tab"
          aria-selected={tab === "sign"}
          className={`tab ${tab === "sign" ? "active" : ""}`}
          onClick={() => setTab("sign")}
          title="Overhead platform sign (8)"
        >
          NS Platform
        </button>
        <button
          role="tab"
          aria-selected={tab === "headways"}
          className={`tab ${tab === "headways" ? "active" : ""}`}
          onClick={() => setTab("headways")}
          title="Headways (9)"
        >
          Headways
        </button>
        <button
          role="tab"
          aria-selected={tab === "bunching"}
          className={`tab ${tab === "bunching" ? "active" : ""}`}
          onClick={() => setTab("bunching")}
          title="Bunching detector (0)"
        >
          Bunching
        </button>
        <button
          role="tab"
          aria-selected={tab === "messages"}
          className={`tab ${tab === "messages" ? "active" : ""}`}
          onClick={() => setTab("messages")}
          title="SMS from trains (-)"
        >
          SMS
        </button>
        <button
          role="tab"
          aria-selected={tab === "bingo"}
          className={`tab ${tab === "bingo" ? "active" : ""}`}
          onClick={() => setTab("bingo")}
          title="Transit Bingo (=)"
        >
          Bingo
        </button>
        <span className="tabs-spacer" aria-hidden />
        <ThemeToggle pref={themePref} onCycle={cycleTheme} />
      </div>

      {/* Both views stay mounted; we toggle visibility so the map keeps its
          GL context and the Stringline history stays continuous. */}
      <div
        className="view"
        style={{ display: tab === "map" ? "block" : "none" }}
      >
        <TransitMap vehicles={vehicles} fitTrigger={fitTrigger} history={historyRef.current} historyVersion={historyVersion} theme={themeEffective} />
        <AlertsBadge />
        <div className="panel">
          <h1>Link Light Rail · live</h1>
          <div className={`status ${connected ? "ok" : "wait"}`}>
            {connected ? "● connected" : CONNECTING_MESSAGES[connectingIdx]}
          </div>
          <div className="total">
            {vehicles.length} {vehicles.length === 1 ? "train" : "trains"} in
            service
          </div>
          {lines.length > 0 && (
            <ul className="lines">
              {lines.map((l) => (
                <li key={l.key}>
                  <div className="line-row">
                    <span
                      className="swatch"
                      style={{ background: l.color }}
                      aria-hidden
                    />
                    <span className="line-label">{l.label}</span>
                    <span className="line-count">{l.count}</span>
                  </div>
                  {l.destinations.length > 0 && (
                    <div className="destinations">
                      {l.destinations.map(([h, c]) => (
                        <div key={h}>
                          {h} {c}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {ageSec !== null && (
            <div className="freshness">last update {ageSec}s ago</div>
          )}
          <button
            className="help-btn"
            onClick={() => setShowHelp((v) => !v)}
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (?)"
          >
            ?
          </button>
        </div>
      </div>

      <div
        className="view"
        style={{ display: tab === "stringline" ? "block" : "none" }}
      >
        <StringlineDiagram
          history={historyRef.current}
          historyVersion={historyVersion}
          connected={connected}
          anchorTs={historyTs}
        />
      </div>

      <div
        className="view"
        style={{ display: tab === "platform" ? "block" : "none" }}
      >
        <PlatformDisplay
          vehicles={vehicles}
          connected={connected}
          historical={historyTs !== null}
        />
      </div>

      <div
        className="view"
        style={{ display: tab === "board" ? "block" : "none" }}
      >
        <DepartureBoard visible={tab === "board"} historyTs={historyTs} />
      </div>

      <div
        className="view"
        style={{ display: tab === "sign" ? "block" : "none" }}
      >
        <PlatformSign visible={tab === "sign"} historyTs={historyTs} />
      </div>

      <div
        className="view"
        style={{ display: tab === "tokyo" ? "block" : "none" }}
      >
        <TokyoLCD
          vehicles={vehicles}
          connected={connected}
          historical={historyTs !== null}
        />
      </div>

      <div
        className="view"
        style={{ display: tab === "nyc" ? "block" : "none" }}
      >
        <NYCCountdown
          vehicles={vehicles}
          connected={connected}
          historical={historyTs !== null}
        />
      </div>

      <div
        className="view"
        style={{ display: tab === "amtrak" ? "block" : "none" }}
      >
        <AmtrakSolari visible={tab === "amtrak"} historyTs={historyTs} />
      </div>

      <div
        className="view"
        style={{ display: tab === "headways" ? "block" : "none" }}
      >
        <Headways
          vehicles={vehicles}
          history={historyRef.current}
          historyVersion={historyVersion}
          connected={connected}
          historical={historyTs !== null}
        />
      </div>

      <div
        className="view"
        style={{ display: tab === "bunching" ? "block" : "none" }}
      >
        <Bunching
          vehicles={vehicles}
          history={historyRef.current}
          historyVersion={historyVersion}
          connected={connected}
          historical={historyTs !== null}
        />
      </div>

      <div
        className="view"
        style={{ display: tab === "messages" ? "block" : "none" }}
      >
        <Messages
          vehicles={vehicles}
          connected={connected}
          historical={historyTs !== null}
        />
      </div>

      <div
        className="view"
        style={{ display: tab === "bingo" ? "block" : "none" }}
      >
        <BingoCard
          vehicles={vehicles}
          history={historyRef.current}
          historyVersion={historyVersion}
          connected={connected}
          historical={historyTs !== null}
        />
      </div>

      {/* Time slider is rendered once at the app level so every tab gets the
          same control; it overlays the active view via position: fixed. */}
      <TimeSlider
        value={historyTs}
        onScrub={(offsetSec) =>
          setHistoryTs(Math.floor(Date.now() / 1000) - offsetSec)
        }
        onLive={() => setHistoryTs(null)}
      />

      <DeployedCommit />

      {showHelp && (
        <div
          className="help-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard shortcuts"
          onClick={() => setShowHelp(false)}
        >
          <div className="help-card" onClick={(e) => e.stopPropagation()}>
            <h2>Keyboard shortcuts</h2>
            <table className="help-table">
              <tbody>
                <tr>
                  <td><kbd>?</kbd></td>
                  <td>Show / hide this help</td>
                </tr>
                <tr>
                  <td><kbd>1</kbd></td>
                  <td>Map view</td>
                </tr>
                <tr>
                  <td><kbd>2</kbd></td>
                  <td>Stringline diagram</td>
                </tr>
                <tr>
                  <td><kbd>3</kbd></td>
                  <td>Tokyo JR LCD</td>
                </tr>
                <tr>
                  <td><kbd>4</kbd></td>
                  <td>NYC countdown clock</td>
                </tr>
                <tr>
                  <td><kbd>5</kbd></td>
                  <td>Amtrak Solari board</td>
                </tr>
                <tr>
                  <td><kbd>6</kbd></td>
                  <td>Seoul Metro displays</td>
                </tr>
                <tr>
                  <td><kbd>7</kbd></td>
                  <td>NS departure board</td>
                </tr>
                <tr>
                  <td><kbd>8</kbd></td>
                  <td>NS overhead platform sign</td>
                </tr>
                <tr>
                  <td><kbd>9</kbd></td>
                  <td>Headways</td>
                </tr>
                <tr>
                  <td><kbd>0</kbd></td>
                  <td>Bunching detector</td>
                </tr>
                <tr>
                  <td><kbd>-</kbd></td>
                  <td>Train messages (SMS)</td>
                </tr>
                <tr>
                  <td><kbd>=</kbd></td>
                  <td>Transit bingo</td>
                </tr>
                <tr>
                  <td><kbd>f</kbd></td>
                  <td>Fit map to all vehicles</td>
                </tr>
                <tr>
                  <td><kbd>Esc</kbd></td>
                  <td>Close this dialog</td>
                </tr>
              </tbody>
            </table>
            <button
              className="help-close"
              onClick={() => setShowHelp(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
